import React, { useEffect, useState, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Card, CardBody, Button, Col, Input, Label, ListGroup, ListGroupItem, Row, Spinner } from "reactstrap";
import SimpleBar from "simplebar-react";
import { Action, ThunkDispatch } from "@reduxjs/toolkit";
import { withTranslation } from "react-i18next";
import { propertyListGetRequest, propertyBulkUpdateRequest } from "store/properties/action";
import { ApplicationState } from "store";

interface ParsedProperty {
    id: string;
    key: string;
    label: string;
    profileCompleteness: boolean;
}

interface ProfileCompletenessProps {
    model: any;
    t: (key: string) => string;
    handleChange: (value: string) => void;
}

const StudentFields = ({ model, t, handleChange }: ProfileCompletenessProps) => {
    const dispatch: ThunkDispatch<any, null, Action<string>> = useDispatch();
    const propertiesList = useSelector((state: ApplicationState) => state.properties.list);

    const [parsedProperties, setParsedProperties] = useState<ParsedProperty[]>([]);
    const [selectedProperties, setSelectedProperties] = useState<ParsedProperty[]>([]);
    const [unselectedProperties, setUnselectedProperties] = useState<ParsedProperty[]>([]);
    const [initialSelected, setInitialSelected] = useState<ParsedProperty[]>([]);
    const [initialUnselected, setInitialUnselected] = useState<ParsedProperty[]>([]);
    const [searchTerm, setSearchTerm] = useState<string>("");
    const [isModified, setIsModified] = useState<boolean>(false);
    const [loading, setLoading] = useState<boolean>(true);

    useEffect(() => {
        if (model) dispatch(propertyListGetRequest(model, { pageSize: "300" }));
    }, [model]);

    useEffect(() => {
        const parsedData = propertiesList
            .map((item: { valuesJson: string; id: any }) => {
                if (item.valuesJson) {
                    try {
                        const values = JSON.parse(item.valuesJson);
                        return {
                            key: values.key,
                            label: values.label,
                            id: item.id,
                            profileCompleteness: values.profileCompleteness,
                        };
                    } catch (error) {
                        console.error("Error parsing valuesJson", error);
                        return null;
                    }
                }
                return null;
            })
            .filter((item: { key: any; label: any; } | null): item is ParsedProperty => item !== null && item.key && item.label);

        setParsedProperties(parsedData);
        setLoading(false);
    }, [propertiesList]);

    useEffect(() => {
        const selected = parsedProperties.filter((property) => property.profileCompleteness === true);
        const unselected = parsedProperties.filter((property) => property.profileCompleteness !== true);

        setSelectedProperties(selected);
        setUnselectedProperties(unselected);
        setInitialSelected(selected);
        setInitialUnselected(unselected);
    }, [parsedProperties]);

    const checkIfModified = useCallback(() => {
        const isSelectedEqual = JSON.stringify(initialSelected) === JSON.stringify(selectedProperties);
        const isUnselectedEqual = JSON.stringify(initialUnselected) === JSON.stringify(unselectedProperties);
        setIsModified(!isSelectedEqual || !isUnselectedEqual);
    }, [initialSelected, initialUnselected, selectedProperties, unselectedProperties]);

    useEffect(() => {
        checkIfModified();
    }, [selectedProperties, unselectedProperties, checkIfModified]);

    const handleCheckboxChange = (property: ParsedProperty, checked: boolean) => {
        if (checked) {
            setUnselectedProperties((prev) => prev.filter((item) => item.key !== property.key));
            setSelectedProperties((prev) => [...prev, property]);
        } else {
            setSelectedProperties((prev) => prev.filter((item) => item.key !== property.key));
            setUnselectedProperties((prev) => [...prev, property]);
        }
    };

    const handleSelectAll = () => {
        if (unselectedProperties.length > 0) {
            setSelectedProperties((prev) => [...prev, ...unselectedProperties]);
            setUnselectedProperties([]);
        } else {
            setSelectedProperties([]);
            setUnselectedProperties(parsedProperties);
        }
    };

    const handleDiscardChanges = () => {
        setUnselectedProperties(initialUnselected);
        setSelectedProperties(initialSelected);
        setSearchTerm("");
    };

    const handleApplyChanges = () => {
        if (selectedProperties.length > 0) {
            const incompleteProperties = selectedProperties.filter((property) => property.profileCompleteness !== true);
            if (incompleteProperties.length > 0) {
                const ids = incompleteProperties.map((p) => p.id).join(",");
                const data = {
                    ids,
                    properties: { profileCompleteness: true },
                };
                dispatch(propertyBulkUpdateRequest(data, () => { }, () => { }));
            }
        }
        if (unselectedProperties.length > 0) {
            const completeProperties = unselectedProperties.filter((property) => property.profileCompleteness === true);
            if (completeProperties.length > 0) {
                const ids = completeProperties.map((p) => p.id).join(",");
                const data = {
                    ids,
                    properties: { profileCompleteness: false },
                };
                dispatch(propertyBulkUpdateRequest(data, () => { }, () => { }));
            }
        }
        setInitialSelected(selectedProperties);
        setInitialUnselected(unselectedProperties);
        setIsModified(false);
    };

    const filteredUnselectedProperties = unselectedProperties.filter((property) =>
        property.label.toLowerCase().includes(searchTerm.toLowerCase())
    );
    const filteredSelectedProperties = selectedProperties.filter((property) =>
        property.label.toLowerCase().includes(searchTerm.toLowerCase())
    );

    if (loading) {
        return (
            <div className="d-flex justify-content-center align-items-center">
                <Spinner color="primary">Loading...</Spinner>
            </div>
        );
    }
    console.log("model", model)
    return (
        <Card className="border-top border-dashed">
            {
                model && (model !== "students" && model !== "applications") ?
                    <CardBody className='border border-dashed border-end-0 border-start-0 vstack gap-2'>
                        <div className='d-flex align-items-center justify-content-center' style={{ height: '300px' }}>
                            <h4 className='text-muted'>{t("userpreferences.selected_model_dont_have_profile_completeness")}</h4>
                        </div>
                    </CardBody>
                    :
                    <CardBody>
                        <div className="d-flex gap-2 justify-content-between align-items-baseline">
                            <div className="d-flex align-items-baseline gap-2">
                                <div className="search-box">
                                    <Input
                                        type="text"
                                        placeholder={t("userpreferences.search_fields")}
                                        value={searchTerm}
                                        onChange={(e) => {
                                            // handleChange(e.target.value)
                                            setSearchTerm(e.target.value)
                                        }
                                        }
                                    />
                                    {searchTerm === "" ? (
                                        <i className="mb-2 ri-search-line search-icon"></i>
                                    ) : (
                                        <i
                                            className="ri-close-circle-fill search-icon cursor-pointer"
                                            onClick={() => setSearchTerm("")}
                                        ></i>
                                    )}
                                </div>
                            </div>
                            <div className="d-flex gap-2 align-items-center">
                                <Label className="mb-3 fs-16">
                                    {filteredSelectedProperties.length === 0 ? "No" : filteredSelectedProperties.length}{" "}
                                    {t("userpreferences.fields_selected")}
                                </Label>
                                <Button onClick={handleSelectAll} color="primary" className="btn-sm mb-3">
                                    {unselectedProperties.length > 0
                                        ? t("userpreferences.select_all")
                                        : t("userpreferences.deselect_all")}
                                </Button>
                                <Button
                                    onClick={handleDiscardChanges}
                                    color="danger"
                                    className="btn-sm mb-3"
                                    disabled={!isModified}
                                >
                                    {t("userpreferences.discard_changes")}
                                </Button>
                                <Button
                                    onClick={handleApplyChanges}
                                    color="success"
                                    className="btn-sm mb-3"
                                    disabled={!isModified}
                                >
                                    {t("userpreferences.apply_changes")}
                                </Button>
                            </div>
                        </div>

                        {filteredUnselectedProperties.length > 0 || filteredSelectedProperties.length > 0 ? (
                            <Row>
                                <Col xl={6} lg={6} sm={6}>
                                    <ListGroup flush>
                                        <SimpleBar autoHide={true} style={{ maxHeight: "500px" }}>
                                            {filteredUnselectedProperties.map((property) => (
                                                <ListGroupItem tag="label" key={property.key}>
                                                    <div className="d-flex gap-4">
                                                        <div>
                                                            <Input
                                                                className="form-check-input mx-1"
                                                                type="checkbox"
                                                                id={property.key}
                                                                name={property.key}
                                                                onChange={(e) => handleCheckboxChange(property, e.target.checked)}
                                                            />
                                                        </div>
                                                        <div className="ms-2">{property.label}</div>
                                                    </div>
                                                </ListGroupItem>
                                            ))}
                                        </SimpleBar>
                                    </ListGroup>
                                </Col>
                                <Col xl={6} lg={6} sm={6}>
                                    <ListGroup flush>
                                        <SimpleBar autoHide={true} style={{ maxHeight: "500px" }}>
                                            {filteredSelectedProperties.map((property) => (
                                                <ListGroupItem tag="label" key={property.key}>
                                                    <div className="d-flex gap-3">
                                                        <div>
                                                            <Input
                                                                className="form-check-input mx-1"
                                                                type="checkbox"
                                                                id={property.key}
                                                                name={property.key}
                                                                checked
                                                                onChange={(e) => handleCheckboxChange(property, e.target.checked)}
                                                            />
                                                        </div>
                                                        <div className="ms-2">{property.label}</div>
                                                    </div>
                                                </ListGroupItem>
                                            ))}
                                        </SimpleBar>
                                    </ListGroup>
                                </Col>
                            </Row>
                        ) : (
                            <div className="d-flex justify-content-center align-items-center">
                                <Spinner color="primary">Loading...</Spinner>
                            </div>
                        )}
                    </CardBody>
            }
        </Card>
    );
};

export default withTranslation()(StudentFields);
